import { useEffect, useMemo, useState } from "react";

import { api } from "@/services/api";
import { cached, CACHE_TTL } from "@/lib/requestCache";
import { isLocode } from "@/lib/formatPort";

const names = new Map<string, string>();
const resolved = new Set<string>();

function normalize(locodes: readonly string[]): string[] {
  const unique = new Set<string>();
  for (const raw of locodes) {
    const locode = raw.trim().toUpperCase();
    if (isLocode(locode)) unique.add(locode);
  }
  return [...unique].sort();
}

function snapshot(locodes: readonly string[]): Map<string, string> {
  const map = new Map<string, string>();
  for (const locode of locodes) {
    const name = names.get(locode);
    if (name) map.set(locode, name);
  }
  return map;
}

function remember(locode: string, name: string | null | undefined) {
  const key = locode.trim().toUpperCase();
  if (!isLocode(key)) return;
  resolved.add(key);
  if (!name?.trim() || names.has(key)) return;
  names.set(key, name.trim());
}

async function resolvePortNames(locodes: string[]): Promise<void> {
  const missing = locodes.filter((locode) => !resolved.has(locode));
  if (missing.length === 0) return;

  const cacheKey = `port-names:${missing.join(",")}`;
  const expansions = await cached(
    cacheKey,
    () => api.getPortAreaExpansion(missing.join(",")),
    CACHE_TTL.reference,
  );

  for (const expansion of expansions.expansions) {
    remember(expansion.requested, expansion.requested_name);
    for (const member of expansion.members) {
      remember(member.locode, member.name);
    }
  }
  for (const locode of missing) {
    resolved.add(locode);
  }
}

/**
 * Resolve UN/LOCODEs to catalogue names. First paint falls back to the locode;
 * a shared cache fills names in as the area-expansion lookup returns.
 */
export function usePortNames(locodes: readonly string[]): ReadonlyMap<string, string> {
  const key = normalize(locodes).join(",");
  const list = useMemo(() => (key ? key.split(",") : []), [key]);
  const [version, setVersion] = useState(0);

  useEffect(() => {
    if (list.length === 0) return;
    let cancelled = false;
    resolvePortNames(list)
      .then(() => {
        if (!cancelled) setVersion((current) => current + 1);
      })
      .catch(() => {
        /* keep locode-only labels */
      });
    return () => {
      cancelled = true;
    };
  }, [key, list]);

  return useMemo(() => snapshot(list), [list, version]);
}
